<template>
  <div>
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title :class="{ 'hidden-sm-and-down': showSearch }">
        Users
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-row v-if="showSearch" align="center" style="max-width: 300px;">
        <v-text-field
          flat
          hide-details
          single-line
          label="Search name, email or phone number"
          v-model="filter.search"
          @input="search"
        >
        </v-text-field>
      </v-row>
      <v-btn class="ml-5" icon @click="showSearch = !showSearch">
        <v-icon>{{ icons.search }}</v-icon>
      </v-btn>
    </v-app-bar>
    <div class="ma-4" id="user-list">
      <v-row align="center">
        <v-col class="d-flex" cols="12" sm="2">
          <v-select
            @change="onLimitChange"
            v-model="filter.limit"
            :items="[10, 25, 50, 100]"
            label="Show"
          />
        </v-col>
        <v-col></v-col>
        <v-col class="d-flex flex-column" cols="12" sm="3">
          <v-btn class="align-self-end" @click="exportExcel" color="primary">
            Export to Excel
          </v-btn>
        </v-col>
      </v-row>
      <v-simple-table>
        <thead>
          <tr>
            <th width="1%"></th>
            <th class="text-left">Name</th>
            <th class="text-left">Email</th>
            <th class="text-left">Phone number</th>
            <th class="text-left">Added date</th>
            <th><span></span></th>
          </tr>
        </thead>
        <tbody>
          <template v-if="users.length">
            <tr v-for="user in users" :key="user.id">
              <td>
                <user-avatar :avatar="user.avatar" size="38" class="ma-2" />
              </td>
              <td>
                <text-highlight :queries="filter.search">
                  {{ user.full_name }}
                </text-highlight>
              </td>
              <td>
                <text-highlight :queries="filter.search">
                  {{ user.email }}
                </text-highlight>
              </td>
              <td>
                <text-highlight :queries="filter.search">
                  {{ user.phone_number ? '+' + user.phone_number : '' }}
                </text-highlight>
              </td>
              <td>{{ user.addedDate }}</td>
              <td class="text-right">
                <v-menu>
                  <template v-slot:activator="{ on }">
                    <v-btn icon v-on="on">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="view(user)">
                      <v-list-item-title>View</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="remove(user)">
                      <v-list-item-title>Remove</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
          <tr v-else>
            <td class="text-center headline grey--text py-10" colspan="6">
              No users available.
            </td>
          </tr>
        </tbody>
      </v-simple-table>
      <v-overlay absolute :value="loading" opacity="0.1">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
      </v-overlay>
    </div>
    <div v-if="paginationLength > 1" class="text-center mt-4">
      <v-pagination
        @input="pageChanged"
        :value="listMeta.current_page"
        :length="paginationLength"
        :total-visible="10"
        circle
      ></v-pagination>
    </div>
    <profile-dialog v-model="showProfile" :profile="selectedProfile" />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { downloadFile } from '@/utils/file'
import { mdiDotsVertical, mdiMagnify } from '@mdi/js'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import ProfileDialog from '@/components/ProfileDialog'
import debounce from 'lodash/debounce'
import TextHighlight from 'vue-text-highlight'
import UserAvatar from '@/components/UserAvatar'

export default {
  name: 'UsersPage',

  components: {
    ProfileDialog,
    TextHighlight,
    AppBarNavIcon,
    UserAvatar
  },

  data() {
    return {
      loading: false,
      showSearch: false,
      showProfile: false,
      selectedProfile: null,
      avatarTimestamp: new Date().getTime(),
      filter: {
        search: '',
        limit: 10,
        page: 1
      },
      icons: {
        settings: mdiDotsVertical,
        search: mdiMagnify
      }
    }
  },

  computed: {
    ...mapState({
      users: state => state.user.list,
      listMeta: state => state.user.listMeta
    }),

    paginationLength() {
      return this.listMeta.last_page
    }
  },

  methods: {
    ...mapActions({
      getUsers: 'user/getUsers'
    }),

    pageChanged(page) {
      this.filter.page = page
      this.fetchUsers()
    },

    search: debounce(function() {
      this.fetchUsers()
    }, 600),

    async fetchUsers() {
      this.loading = true
      await this.getUsers(this.filter)
      this.loading = false
    },

    showProfileDialog(profile) {
      this.selectedProfile = profile
      this.showProfile = true
    },

    view(user) {
      this.$router.push({ name: 'user.details', params: { id: user.id } })
    },

    onLimitChange(limit) {
      this.filter.page = 1
      this.filter.limit = limit
      this.fetchUsers()
    },

    exportExcel() {
      this.$api
        .get('/users/export', {
          params: {
            limit: this.listMeta.per_page,
            page: this.listMeta.current_page,
            'filter[search]': this.filter.search
          },
          responseType: 'blob'
        })
        .then(({ data }) => {
          downloadFile(
            data,
            'users.xlsx',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          )
        })
    },

    remove(user) {
      this.$confirm(`Are you sure you want to remove this user?`, {
        color: 'primary',
        title: 'Remove user'
      }).then(confirm => {
        if (confirm) {
          user.delete().then(() => {
            this.fetchUsers()
          })
        }
      })
    }
  },

  created() {
    this.fetchUsers()
  }
}
</script>
